import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";

import {
  User,
  Heart,
  ShoppingBag,
  List,
} from '@phosphor-icons/react';

const COMMUNITY_MENU_ORDER = [
  "자유게시판",
  "질문게시판",
  "농사정보",
  "스마트팜",
];

const CommunityNavbar = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { user, logout } = useAuth();
  const { cartCount, fetchCartCount, clearCart } = useCart();

  const [isSideOpen, setIsSideOpen] = useState(false);
  const [hideNavbar, setHideNavbar] = useState(false);

  const [boards, setBoards] = useState([]);
  const [boardsLoading, setBoardsLoading] = useState(true);

  const searchParams = new URLSearchParams(location.search);
  const currentBoard = searchParams.get("board") || "";

  const communityMenus = useMemo(() => {
    const boardList = Array.isArray(boards) ? boards : [];

    const orderedBoards = COMMUNITY_MENU_ORDER
      .map((boardName) =>
        boardList.find((board) => board.board_name === boardName)
      )
      .filter(Boolean);

    const remainingBoards = boardList.filter(
      (board) => !COMMUNITY_MENU_ORDER.includes(board.board_name)
    );

    return [
      { key: "", label: "전체", to: "/community" },
      ...[...orderedBoards, ...remainingBoards].map((board) => ({
        key: String(board.board_code || ""),
        label: board.board_name,
        to: `/community?board=${encodeURIComponent(board.board_code || "")}`,
      })),
    ];
  }, [boards]);

  useEffect(() => {
    let lastScrollY = window.scrollY;

    const isMobile = () => window.innerWidth <= 767.98;

    const handleScroll = () => {
      if (!isMobile()) {
        setHideNavbar(false);
        return;
      }

      const currentScrollY = window.scrollY;

      if (currentScrollY > lastScrollY && currentScrollY > 80) {
        setHideNavbar(true);
      } else {
        setHideNavbar(false);
      }

      lastScrollY = currentScrollY;
    };

    const handleResize = () => {
      if (!isMobile()) {
        setHideNavbar(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    setBoardsLoading(true);

    axios
      .get("/api/boards", { withCredentials: true })
      .then((res) => {
        if (!mounted) return;

        const data = res?.data?.data ?? res?.data;
        setBoards(Array.isArray(data?.boards) ? data.boards : []);
      })
      .catch((err) => {
        console.error("커뮤니티 게시판 목록 로드 실패:", err);

        if (mounted) {
          setBoards([]);
        }
      })
      .finally(() => {
        if (mounted) {
          setBoardsLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (user) {
      fetchCartCount();
    } else {
      clearCart();
    }
  }, [user, fetchCartCount, clearCart]);

  const handleLogout = async () => {
    if (!window.confirm("로그아웃 하시겠습니까?")) return;

    try {
      await axios.post(
        "/api/auth/logout",
        {},
        { withCredentials: true }
      );

      logout();
      setIsSideOpen(false);
      navigate("/");
    } catch (err) {
      console.error("로그아웃 실패:", err);
    }
  };

  const closeSideMenu = () => {
    setIsSideOpen(false);
  };

  return (
    <>
      <link
        rel="stylesheet"
        href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.10.5/font/bootstrap-icons.css"
      />

      <style>{`
        .community-notice-strip {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          height: 28px;
          background: #eeeeee;
          color: #000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 12px;
          font-size: 12px;
          font-weight: 600;
          z-index: 1200;
          overflow: hidden;
        }

        .community-notice-strip a {
          display: block;
          max-width: 100%;
          color: #000;
          text-decoration: none;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .community-top-bar {
          position: fixed;
          top: 28px;
          left: 0;
          right: 0;
          z-index: 1100;
          height: 60px;
          padding: 0 0.76rem;
          background: #fff;
          border-bottom: 1px solid #dee2e6;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .community-logo-wrap {
          display: flex;
          align-items: center;
          min-width: 140px;
        }

        .community-back-btn {
          border: 0;
          background: transparent;
          padding: 0;
          margin-right: 8px;
          color: #222;
          cursor: pointer;
          line-height: 1;
        }

        .community-title {
          color: #000;
          font-size: 1.1rem;
          font-weight: 800;
          white-space: nowrap;
        }

        .community-pc-menu {
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 30px;
          overflow: hidden;
        }

        .community-pc-link {
          position: relative;
          height: 60px;
          display: flex;
          align-items: center;
          color: #333;
          font-size: 14px;
          font-weight: 650;
          text-decoration: none;
          white-space: nowrap;
        }

        .community-pc-link:hover,
        .community-pc-link:focus,
        .community-pc-link:active {
          color: #000;
          text-decoration: none;
        }

        .community-pc-link.active {
          color: #000;
          font-weight: 800;
        }

        .community-pc-link.active::after {
          content: "";
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
          height: 2px;
          background: #000;
        }

        .community-icons {
          min-width: 140px;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 1.1rem;
        }

        .community-icons svg {
          display: block;
          color: #222;
          cursor: pointer;
        }

        .community-icon-link {
          color: inherit;
          text-decoration: none;
        }

        .community-cart-wrapper {
          position: relative;
          display: inline-flex;
          align-items: center;
        }

        .community-cart-badge {
          position: absolute;
          top: -8px;
          right: -8px;
          min-width: 18px;
          height: 18px;
          padding: 0 4px;
          background: #dc3545;
          color: #fff;
          border-radius: 999px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          font-weight: 800;
          line-height: 1;
        }

        .community-mobile-menu {
          display: none;
        }

        .community-navbar-spacer {
          height: 88px;
        }

        .community-side-menu {
          position: fixed;
          top: 0;
          left: -280px;
          width: 280px;
          height: 100vh;
          padding: 18px;
          background: #fff;
          box-shadow: 2px 0 10px rgba(0, 0, 0, 0.16);
          z-index: 2000;
          display: flex;
          flex-direction: column;
          overflow-y: auto;
          transition: left 0.25s ease;
        }

        .community-side-menu.show {
          left: 0;
        }

        .community-side-close {
          align-self: flex-end;
          border: 0;
          background: transparent;
          color: #666;
          font-size: 34px;
          line-height: 1;
          cursor: pointer;
        }

        .community-side-user {
          margin: 10px 0 14px;
          font-weight: 700;
          color: #222;
        }

        .community-side-account {
          margin-bottom: 14px;
          padding-bottom: 14px;
          border-bottom: 1px solid #eee;
          font-size: 14px;
        }

        .community-side-account a,
        .community-side-account button {
          border: 0;
          background: transparent;
          padding: 0;
          color: #0d6efd;
          text-decoration: none;
          cursor: pointer;
        }

        .community-side-divider {
          margin: 0 8px;
          color: #ccc;
        }

        .community-side-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .community-side-item {
          border-bottom: 1px solid #eee;
        }

        .community-side-link {
          position: relative;
          display: block;
          padding: 15px 2px;
          color: #333;
          font-size: 15px;
          font-weight: 650;
          text-decoration: none;
        }

        .community-side-link:hover,
        .community-side-link:focus,
        .community-side-link:active {
          color: #000;
          text-decoration: none;
        }

        .community-side-link.active {
          color: #000;
          font-weight: 800;
        }

        .community-side-link.active::after {
          content: "";
          position: absolute;
          right: 2px;
          top: 50%;
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: #000;
          transform: translateY(-50%);
        }

        .community-side-utility {
          margin-top: 18px;
          padding-top: 4px;
        }

        .community-side-utility a {
          display: block;
          padding: 11px 2px;
          color: #555;
          font-size: 14px;
          text-decoration: none;
        }

        .community-overlay {
          position: fixed;
          inset: 0;
          z-index: 1500;
          background: rgba(0, 0, 0, 0.48);
        }

        .community-notice-strip,
        .community-top-bar,
        .community-mobile-menu {
          transition: transform 0.25s ease;
        }

        .community-mobile-only {
          display: none;
        }

        @media (max-width: 767.98px) {
          .community-pc-only {
            display: none !important;
          }

          .community-mobile-only {
            display: block !important;
          }

          .community-top-bar {
            padding: 0 0.76rem;
            border-bottom: 0;
          }

          .community-logo-wrap {
            min-width: 0;
          }

          .community-title {
            font-size: 1rem;
          }

          .community-icons {
            min-width: 0;
            gap: 1.1rem;
          }

          .community-mobile-menu {
            position: fixed;
            top: 88px;
            left: 0;
            right: 0;
            z-index: 1050;
            height: 40px;
            padding: 0 14px;
            gap: 28px;
            background: #fff;
            border-bottom: 1px solid #e5e5e5;
            display: flex;
            align-items: center;
            overflow-x: auto;
            overflow-y: hidden;
            white-space: nowrap;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
          }

          .community-mobile-menu::-webkit-scrollbar {
            display: none;
          }

          .community-mobile-link {
            position: relative;
            flex: 0 0 auto;
            min-width: max-content;
            height: 40px;
            display: flex;
            align-items: center;
            color: #222;
            font-size: 14px;
            font-weight: 650;
            text-decoration: none;
          }

          .community-mobile-link:hover,
          .community-mobile-link:focus,
          .community-mobile-link:active {
            color: #000;
            text-decoration: none;
          }

          .community-mobile-link.active {
            color: #000;
            font-weight: 800;
          }

          .community-mobile-link.active::after {
            content: "";
            position: absolute;
            left: 0;
            right: 0;
            bottom: 0;
            height: 2px;
            background: #000;
          }

          .community-navbar-spacer {
            height: 128px;
          }

          .community-navbar-hidden .community-notice-strip {
            transform: translateY(-28px);
          }

          .community-navbar-hidden .community-top-bar {
            transform: translateY(-88px);
          }

          .community-navbar-hidden .community-mobile-menu {
            transform: translateY(-128px);
          }
        }

        @media (min-width: 768px) {
          .community-pc-only {
            display: flex !important;
          }
        }
      `}</style>

      <div className={hideNavbar ? "community-navbar-hidden" : ""}>

        <header className="community-top-bar">
          <div className="community-logo-wrap">
            <button
              type="button"
              className="community-back-btn"
              onClick={() => navigate("/")}
              aria-label="홈으로 이동"
            >
              <i className="bi bi-arrow-left" />
            </button>

            <span style={{ fontWeight: "800" }}>콩꼬물 커뮤니티</span>
          </div>

          <nav className="community-pc-menu community-pc-only">
            {!boardsLoading &&
              communityMenus.map((menu) => (
                <Link
                  key={menu.key || "all"}
                  to={menu.to}
                  className={`community-pc-link ${
                    currentBoard === menu.key ? "active" : ""
                  }`}
                >
                  {menu.label}
                </Link>
              ))}
          </nav>

          <div className="community-icons">
            <User
              size={19}
              strokeWidth={1.7}
              onClick={() => navigate("/mypage")}
              aria-label="마이페이지"
            />

            <Link
              to="/wishlist"
              className="community-icon-link community-cart-wrapper"
              aria-label="찜 목록"
            >
              <Heart size={20} strokeWidth={1.7} />
            </Link>

            <Link
              to="/cart"
              className="community-icon-link community-cart-wrapper"
              aria-label="장바구니"
            >
              <ShoppingBag size={20} strokeWidth={1.7} />

              {cartCount > 0 && (
                <span className="community-cart-badge">
                  {cartCount > 99 ? "99+" : cartCount}
                </span>
              )}
            </Link>

            <List
              className="community-mobile-only"
              size={20}
              strokeWidth={1.7}
              onClick={() => setIsSideOpen(true)}
              aria-label="메뉴 열기"
            />
          </div>
        </header>

        <nav className="community-mobile-menu">
          {!boardsLoading &&
            communityMenus.map((menu) => (
              <Link
                key={menu.key || "all"}
                to={menu.to}
                className={`community-mobile-link ${
                  currentBoard === menu.key ? "active" : ""
                }`}
              >
                {menu.label}
              </Link>
            ))}
        </nav>
      </div>

      <aside
        className={`community-side-menu ${isSideOpen ? "show" : ""}`}
      >
        <button
          type="button"
          className="community-side-close"
          onClick={closeSideMenu}
          aria-label="메뉴 닫기"
        >
          &times;
        </button>

        <div className="community-side-user">
          {user
            ? `${user.nickname || "회원"}님 환영합니다.`
            : "방문자님 환영합니다."}
        </div>

        <div className="community-side-account">
          {user ? (
            <button type="button" onClick={handleLogout}>
              로그아웃
            </button>
          ) : (
            <Link to="/login" onClick={closeSideMenu}>
              로그인
            </Link>
          )}

          <span className="community-side-divider">|</span>

          <Link to="/mypage" onClick={closeSideMenu}>
            마이페이지
          </Link>
        </div>

        <ul className="community-side-list">
          {!boardsLoading &&
            communityMenus.map((menu) => (
              <li
                key={menu.key || "all"}
                className="community-side-item"
              >
                <Link
                  to={menu.to}
                  onClick={closeSideMenu}
                  className={`community-side-link ${
                    currentBoard === menu.key ? "active" : ""
                  }`}
                >
                  {menu.label}
                </Link>
              </li>
            ))}
        </ul>

        <div className="community-side-utility">
          <Link to="/community/write" onClick={closeSideMenu}>
            글쓰기
          </Link>

          <Link to="/community/my-posts" onClick={closeSideMenu}>
            내가 쓴 글
          </Link>

          <Link to="/community/bookmarks" onClick={closeSideMenu}>
            북마크
          </Link>

          <Link to="/shop" onClick={closeSideMenu}>
            쇼핑몰
          </Link>
        </div>
      </aside>

      {isSideOpen && (
        <div
          className="community-overlay"
          onClick={closeSideMenu}
        />
      )}

      <div className="community-navbar-spacer" />
    </>
  );
};

export default CommunityNavbar;